import React from 'react';
import { Facebook, Instagram, Linkedin, Mail, Phone, Clock, MapPin, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface FooterProps {
  className?: string;
}

const empresasLinks = [
  // Plataformas
  { title: "Nexus ATS", path: "/nexus-ats" },
  { title: "Elempleo.com", path: "/elempleo" },
  { title: "Orienta360", path: "/orienta-360" },
  { title: "Nexus Languages", path: "/nexus-languages" },
  { title: "Talent Connect", path: "/talent-connect" },
  // Servicios
  { title: "Reclutamiento y Selección", path: "/reclutamiento-seleccion" },
  { title: "Pruebas Psicométricas", path: "/pruebas-psicometricas" },
  { title: "Encuesta Salarial", path: "/encuesta-salarial" },
  { title: "Encuesta de Clima y Cultura", path: "/encuesta-clima-cultura" },
];

const candidatesLinks = [
  { title: "Ofertas de empleo", href: "https://jobs.nexushr.tech/", external: true },
  { title: "Registrar CV", path: "/registrar-cv" },
  { title: "Calculadora salarial", path: "/calculadora-salarial" },
  { title: "Descargables", path: "/descargables" },
];

const resourcesLinks = [
  { title: "Blog", path: "/blog" },
  { title: "Sobre nosotros", path: "/nosotros" },
  { title: "Política de Privacidad", path: "/politica-de-privacidad" },
  { title: "Términos y Condiciones", path: "/terminos-y-condiciones" },
];

const sustainabilityLinks = [
  { title: "Política Ambiental", path: "/politica-ambiental" },
  { title: "Política de No Discriminación y Equidad", path: "/politica-no-discriminacion" },
  { title: "Reporte de Sostenibilidad 2026", path: "/reporte-sostenibilidad-2026" },
];

const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61575010856485", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/nexushr.cr/", label: "Instagram" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/nexus-hr-costa-rica/", label: "LinkedIn" },
];

const Footer: React.FC<FooterProps> = ({ className }) => {
  return (
    <footer className={cn("text-white", className)} style={{ background: "#620AE2" }}>
      <div className="container mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">

          {/* Column 1: Logo + Contact */}
          <div className="col-span-2 md:col-span-2 lg:col-span-1">
            <img
              src="/lovable-uploads/NEXUS-BLANCO-.png"
              alt="Nexus HR Logo"
              className="h-10 w-auto mb-5"
              loading="lazy"
              decoding="async"
            />
            <ul className="space-y-3 text-sm text-white/90">
              <li>
                <a href="tel:+50688862011" className="flex items-start gap-2 hover:text-white transition-colors">
                  <Phone size={16} className="flex-shrink-0 mt-0.5" />
                  <span>+506 8886-2011</span>
                </a>
              </li>
              <li>
                <a href="mailto:soporte@nexushrcr.com" className="flex items-start gap-2 hover:text-white transition-colors">
                  <Mail size={16} className="flex-shrink-0 mt-0.5" />
                  <span>soporte@nexushrcr.com</span>
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="flex-shrink-0 mt-0.5" />
                <span>Costa Rica, San José, Escazú, Bello Horizonte</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={16} className="flex-shrink-0 mt-0.5" />
                <span>Lun-Vie 8:00AM - 5:00PM</span>
              </li>
            </ul>
            <div className="flex items-center gap-3 mt-5">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border-[1.5px] border-white bg-transparent text-white flex items-center justify-center transition-all duration-200 ease-[ease] hover:bg-white hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                  aria-label={`Seguir en ${social.label}`}
                >
                  <social.icon size={16} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Para Empresas */}
          <div>
            <h3 className="text-base font-bold mb-4">Para Empresas</h3>
            <ul className="space-y-2">
              {empresasLinks.map((link) => (
                <li key={link.title}>
                  <Link to={link.path} className="text-sm text-white/85 hover:text-white hover:underline underline-offset-4 transition-all duration-200">
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Candidatos */}
          <div>
            <h3 className="text-base font-bold mb-4">Candidatos</h3>
            <ul className="space-y-2">
              {candidatesLinks.map((link) => (
                <li key={link.title}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-white/85 hover:text-white hover:underline underline-offset-4 transition-all duration-200 inline-flex items-center gap-1"
                    >
                      {link.title}
                      <ExternalLink size={12} aria-hidden="true" />
                    </a>
                  ) : (
                    <Link to={link.path!} className="text-sm text-white/85 hover:text-white hover:underline underline-offset-4 transition-all duration-200">
                      {link.title}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Recursos + Legal */}
          <div>
            <h3 className="text-base font-bold mb-4">Recursos</h3>
            <ul className="space-y-2">
              {resourcesLinks.map((link) => (
                <li key={link.title}>
                  <Link to={link.path} className="text-sm text-white/85 hover:text-white hover:underline underline-offset-4 transition-all duration-200">
                    {link.title}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://jobs.nexushr.tech/nexus-hr-tech-jrz7oqyl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/85 hover:text-white hover:underline underline-offset-4 transition-all duration-200 inline-flex items-center gap-1"
                >
                  Trabaje con Nosotros
                  <ExternalLink size={12} aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>

          {/* Column 6: Sostenibilidad */}
          <div>
            <h3 className="text-base font-bold mb-4">Sostenibilidad</h3>
            <ul className="space-y-2">
              {sustainabilityLinks.map((link) => (
                <li key={link.title}>
                  <Link to={link.path} className="text-sm text-white/85 hover:text-white hover:underline underline-offset-4 transition-all duration-200">
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/15">
        <div className="container mx-auto px-4 md:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-white/80 text-xs">
            © {new Date().getFullYear()} Nexus HR. Todos los derechos reservados.
          </p>
          <p className="text-white/80 text-xs">nexushr.tech</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
